import { useMemo } from 'react';

import {
  ageFromBucket,
  sessionEnergyReadout,
  type EffortScale,
  type WorkoutEnergyReadout,
} from '@jackedlogs/shared';

import type { LoggerDraft } from './state/loggerDraftTypes';
import type { LoggerProfile } from './hostDeps';
import type { EditingTarget } from './LoggerScreen';
import { useLiveWorkoutEnergy } from './useLiveWorkoutEnergy';
import { useHcWinsCalories } from './state/hcCaloriesPreference';

// HC-8 — the workout energy readout for the logger footer + the post-save card.
// Extracted from useLoggerScreen (500-line cap), the same "compose small hooks"
// shape that file uses. Two sources, resolved at READ:
//   • editing a SAVED + ENRICHED session (mode!=='repeat', carries measured
//     calories/HR from HC) → resolve from the STORED fields so the athlete sees
//     the MEASURED value + effort, not a live re-estimate (B2c). This is what
//     gives the "HC wins" toggle teeth on mobile.
//   • otherwise (live logging, a repeat clone, or a plain saved session) → the
//     live estimate from the draft; there's no measured HR live, so effort stays
//     absent (hrMaxPct null).
// The hcWins preference is read ONCE here and threaded into the live estimator
// so the two paths don't each subscribe to it independently.
export function useWorkoutEnergyReadout(
  draft: LoggerDraft,
  effortScale: EffortScale,
  profile: LoggerProfile,
  editing: EditingTarget | null,
): WorkoutEnergyReadout | null {
  const hcWins = useHcWinsCalories().enabled;
  const liveResolved = useLiveWorkoutEnergy(draft, effortScale, profile, hcWins);
  return useMemo<WorkoutEnergyReadout | null>(() => {
    const w = editing?.workout;
    const enriched =
      w !== undefined &&
      editing?.mode !== 'repeat' &&
      (w.calories !== undefined ||
        w.avgHrBpm !== undefined ||
        w.peakHrBpm !== undefined);
    if (enriched) {
      return sessionEnergyReadout(w, {
        hcWins,
        ageYears: ageFromBucket(profile.ageBucket),
      });
    }
    return liveResolved ? { resolved: liveResolved, hrMaxPct: null } : null;
  }, [editing?.workout, editing?.mode, hcWins, profile.ageBucket, liveResolved]);
}
